import { useMemo, useState } from "react";
import { listAuditLogs } from "./api";
import { DateRange, SortTh } from "./ListControls";
import { nextSort, type SortState } from "./list-sort";
import { usePaginatedResource } from "./use-paginated-resource";
import { EmptyRow } from "../dashboard/ui";
import { formatDateTime } from "../dashboard/format";
import { Pagination } from "../../components/Pagination";
import { SkeletonRows } from "../../components/Skeleton";
import { downloadCsv } from "../../lib/csv";

function parseVisitId(raw: string): number | undefined {
  const value = Number(raw);
  return raw.trim() && Number.isInteger(value) && value > 0 ? value : undefined;
}

export function AuditPanel() {
  const [visitIdInput, setVisitIdInput] = useState("");
  const [eventType, setEventType] = useState("");
  const [source, setSource] = useState("");
  const [quickSearch, setQuickSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState<SortState | null>(null);

  const visitId = parseVisitId(visitIdInput);
  const onSort = (col: string) => setSort((current) => nextSort(current, col));

  const { rows, loading, error, page, hasNext, refresh, nextPage, prevPage } = usePaginatedResource(
    (limit, offset) =>
      listAuditLogs({
        visit_id: visitId,
        event_type: eventType.trim() || undefined,
        source: source.trim() || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        sort_dir: sort?.by === "event_time" ? sort.dir : undefined,
        limit,
        offset,
      }),
    [visitId, eventType, source, dateFrom, dateTo, sort],
  );

  const visibleRows = useMemo(() => {
    const needle = quickSearch.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((entry) =>
      [entry.visitor_name, entry.event_type, entry.source, entry.visit_status]
        .filter(Boolean)
        .some((field) => field!.toLowerCase().includes(needle)),
    );
  }, [rows, quickSearch]);

  function exportCsv() {
    downloadCsv(
      "audit-logs.csv",
      visibleRows.map((entry) => ({
        id: entry.id,
        event_time: entry.event_time ?? "",
        visit_id: entry.visit_id,
        event_type: entry.event_type,
        source: entry.source ?? "",
        visitor_name: entry.visitor_name ?? "",
        host_id: entry.host_id ?? "",
        visit_status: entry.visit_status ?? "",
      })),
    );
  }

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Audit Logs</h2>
        <span>{visibleRows.length} rows</span>
      </header>

      <div className="filter-row">
        <input
          className="search-input"
          value={quickSearch}
          onChange={(event) => setQuickSearch(event.target.value)}
          placeholder="Search this page (visitor, event, source, status)"
          aria-label="Quick search audit logs"
        />
        <input
          className="search-input"
          type="number"
          min={1}
          value={visitIdInput}
          onChange={(event) => setVisitIdInput(event.target.value)}
          placeholder="Filter by visit ID"
          aria-label="Filter by visit ID"
        />
        <input
          className="search-input"
          value={eventType}
          onChange={(event) => setEventType(event.target.value)}
          placeholder="Filter by event type"
          aria-label="Filter by event type"
        />
        <input
          className="search-input"
          value={source}
          onChange={(event) => setSource(event.target.value)}
          placeholder="Filter by source"
          aria-label="Filter by source"
        />
        <button className="refresh-btn" type="button" onClick={() => void refresh()} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
        <button className="refresh-btn" type="button" onClick={exportCsv} disabled={visibleRows.length === 0}>
          Export CSV
        </button>
        <DateRange from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo} label="Event" />
      </div>

      {error ? <p className="error-banner">{error}</p> : null}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <SortTh label="Time" col="event_time" sort={sort} onSort={onSort} />
              <th>Visit</th>
              <th>Event Type</th>
              <th>Source</th>
              <th>Visitor</th>
              <th>Host ID</th>
              <th>Visit Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows rows={4} colSpan={7} />
            ) : visibleRows.length === 0 ? (
              <EmptyRow label="No audit logs found" colSpan={7} />
            ) : (
              visibleRows.map((entry) => (
                <tr key={`audit-${entry.id}`}>
                  <td>{formatDateTime(entry.event_time)}</td>
                  <td>#{entry.visit_id}</td>
                  <td>{entry.event_type}</td>
                  <td>{entry.source || "-"}</td>
                  <td>{entry.visitor_name || "-"}</td>
                  <td>{entry.host_id ?? "-"}</td>
                  <td>{entry.visit_status || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} hasNext={hasNext} onPrev={prevPage} onNext={nextPage} disabled={loading} />
    </article>
  );
}
